/**
 * LinkButton Component - HELIX Professional Design System v9.2.1
 *
 * Button-styled Link component for navigation actions.
 * Uses same variants and sizes as Button for consistency.
 */

import Link from 'next/link';
import * as React from 'react';
import { cn } from '@/lib/cn';

type LinkButtonVariant = 'default' | 'secondary' | 'outline' | 'ghost';
type LinkButtonSize = 'sm' | 'md' | 'lg';

const variantClasses: Record<LinkButtonVariant, string> = {
  default:
    'bg-primary text-primary-foreground border border-primary hover:bg-primary/90 active:bg-primary/80',
  secondary:
    'bg-background text-foreground border border-border hover:bg-muted active:bg-muted/80',
  outline:
    'bg-transparent text-foreground border border-border hover:bg-muted hover:text-foreground',
  ghost: 'bg-transparent text-foreground hover:bg-muted hover:text-foreground',
};

const sizeClasses: Record<LinkButtonSize, string> = {
  sm: 'h-8 px-3 text-xs rounded-md gap-1.5',
  md: 'h-9 px-4 text-sm rounded-md gap-2',
  lg: 'h-10 px-5 text-sm rounded-lg gap-2',
};

export interface LinkButtonProps extends React.ComponentProps<typeof Link> {
  variant?: LinkButtonVariant;
  size?: LinkButtonSize;
}

export function LinkButton({
  className,
  variant = 'default',
  size = 'md',
  ...props
}: LinkButtonProps) {
  return (
    <Link
      className={cn(
        'inline-flex items-center justify-center font-medium transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
        variantClasses[variant],
        sizeClasses[size],
        className,
      )}
      {...props}
    />
  );
}
